import React, { useContext, useEffect } from 'react';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { Grid } from '@mui/material';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import Button from '@cpg-its/ads.common.button';
import Banner from '@cpg-its/ads.containers.banner';
import Tooltip from '@cpg-its/ads.common.tooltip';
import Field from '@cpg-its/ads.form.field';
import { useDispatch, useSelector } from 'react-redux';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { tcsmColors } from '../../../global/constants/theme/theme-constants/colors';
import { YesNoIndicators } from '../../../global/models/api-model';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { coverageExpired, getOfferingsOptions, getTierLabel } from '../helpers/annual-enrollment-helpers';
import { AnnualEnrollmentFormValues } from '../models/annual-enrollment-form-models';
import { AEPlanType, AERelationshipCode, AnnualEnrollmentState, ProcessingStatus, SelfText } from '../models/annual-enrollment-models';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormGroup } from '../../../global/components/layout/form-group';
import { CustomField } from '../../../global/components/form-element/custom-field';
import { RadioTable } from '../../../global/components/form-element/radio-table';
import { apiErrorClear } from '../../../global/ducks/base-api';
import { PageHeader } from '../../../global/components/layout/page-header';
import { getAnnualEnrollmentInfo, postAnnualEnrollment } from '../../../global/ducks/mlps-api';
import { setAEInfo } from '../annual-enrollment-slice';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { ButtonWrapper } from '../../../global/components/layout/button-wrapper';
import { AnnualEnrollmentSummary } from './annual-enrollment-summary';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { useBasePath } from '../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../arc/constants/routes';
import { LabelSettingProps } from '../../../global/models/epi-model';


export const AbandonAction = 'Abandon';
export const DeleteAction = 'Delete';

interface Props {
	basePath?: string;
}

export function AnnualEnrollmentAdminConfirmation(props: Props) {
	const app = useContext(DemographicsContext);

	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const medicalEnrollment = AEState.AEInfo?.enrollments_edited?.M && !AEState.AEInfo?.enrollments_edited?.M[0]?.termination_date ? AEState.AEInfo?.enrollments_edited?.M[0] : null;
	const dentalEnrollment = AEState.AEInfo?.enrollments_edited?.D && !AEState.AEInfo?.enrollments_edited?.D[0]?.termination_date ? AEState.AEInfo?.enrollments_edited?.D[0] : null;
	const annualEnrollmentProps = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps.annualEnrollmentSettingsState?.epiContent);
	const medicalExpired = coverageExpired(AEPlanType.Medical, AEState.AEInfo?.ae_defaults, false);
	const dentalExpired = coverageExpired(AEPlanType.Dental, AEState.AEInfo?.ae_defaults, false);
	const guid = useSelector<TcsmStates, string>(state => state.employmentState?.currentEmployee?.guid);
	const isHeldForReview = AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.InProgress && AEState.AEInfo?.form_record?.processing_substatus === ProcessingStatus.HeldForReview;
	const isHeldMessages = [].concat(AEState.AEInfo?.processing_messages?.mc_holds)?.concat(AEState.AEInfo?.processing_messages?.rq_holds)?.concat(AEState.AEInfo?.processing_messages?.tx_holds)?.filter(a => !!a);
	const dispatch = useDispatch();
	const history = useHistory();
	const [open, setOpen] = React.useState(false);

	const buttonClasses = buttonStyles();
	const cpgHome = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);
	const mapHome = useBasePath(`${ArcRoutes.ArcRoot}`);

	const timeString = AEState?.AEInfo?.form_info?.change_id;
	const date = new Date(`${timeString.substring(0, 4)}-${timeString.substring(4, 6)}-${timeString.substring(6, 8)} ${timeString.substring(8, 10)}:${timeString.substring(10, 12)}:${timeString.substring(12, 14)}`);
	const formatedDate = date.toLocaleString('en-US', {
		weekday: 'short', // long, short, narrow
		day: 'numeric', // numeric, 2-digit
		year: 'numeric', // numeric, 2-digit
		month: 'short', // numeric, 2-digit, long, short, narrow
		hour: 'numeric', // numeric, 2-digit
		minute: 'numeric', // numeric, 2-digit
	});

	const isArc = app === DemographicAppKeys.arc;
	let pageTitle = annualEnrollmentProps.confirmationPageTitle;
	let pageDescription = annualEnrollmentProps.confirmationPageDescription;

	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	const groupId = useSelector<TcsmStates, number>(state => state?.groupsState?.currentGroup?.group_id);

	if (isHeldForReview) {
		pageTitle = isArc ? annualEnrollmentProps.adminHeldTitle : annualEnrollmentProps.heldTitle;
		pageDescription = isArc ? annualEnrollmentProps.adminHeldDescription : annualEnrollmentProps.heldDescription;
	}

	if (pageDescription) {
		pageDescription = pageDescription.replace('{date}', formatedDate);
	}

	const handleClose = () => {
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
		setOpen(true);
	};

	useEffect(() => {
		dispatch(apiErrorClear());
	});

	const abandonFormClick = () => {
		dispatch(postAnnualEnrollment(
			guid,
			{
				form_info: {
					change_id: AEState?.AEInfo?.form_info?.change_id,
					change_id_signature: AEState?.AEInfo?.form_info?.change_id_signature,
					workflow_action: AEState?.AEInfo?.form_info?.available_workflow_actions.Abandon ? AbandonAction : DeleteAction,
				},
			},
			() => {
				dispatch(getAnnualEnrollmentInfo(
					guid,
					0,
					(response) => {
						dispatch(setAEInfo(response, app));
						history.push(`${props.basePath}${AccountInfoRoutes.PersonalInfoSegment}`);
					},
					null,
					groupId,
				));

			}));
	};

	const getInitialCoverageValues = (typeKey: AEPlanType) => {
		const coverage: string[] = [];

		if (AEState.AEInfo?.enrollments_edited) {
			if (AEState.AEInfo?.enrollments_base[typeKey]?.length > 0) {
				coverage.push(SelfText);
			}

			const eligibleDependents = AEState.AEInfo?.eligible_dependents;
			for (let i = 0; i < eligibleDependents?.length ?? 0; i += 1) {
				const dependentGuid = eligibleDependents[i]?.person?._guid;
				if (AEState.AEInfo?.dep_enrollments_edited[dependentGuid][typeKey] === YesNoIndicators.Yes) {
					coverage.push(dependentGuid);
				}
			}
		}

		return coverage;
	};

	const initialValues: AnnualEnrollmentFormValues = {
		[AEPlanType.Medical]: getInitialCoverageValues(AEPlanType.Medical),
		[AEPlanType.Dental]: getInitialCoverageValues(AEPlanType.Dental),

		medicalPlan: medicalEnrollment?.code || '',
		dentalPlan: dentalEnrollment?.code || '',

		agreement: false,
		kaiserArbitrationAgreement: false,

		declinedMedicalCoverageReason: '',
		declinedMedicalCoverageReasonOther: '',
	};


	const getSecondaryLabel = (checked: boolean, groupName: AEPlanType, value: string) => {
		if (checked && !initialValues[groupName]?.includes(value)) {
			return [annualEnrollmentProps?.addedText, tcsmColors.green];
		}

		if (!checked && initialValues[groupName]?.includes(value)) {
			return [annualEnrollmentProps?.removedText, tcsmColors.red];
		}

		return null;
	};

	const getRelationshipLabel = (code: AERelationshipCode) => {
		switch (code) {
			case AERelationshipCode.Child:
				return annualEnrollmentProps?.childRelationLabel;
			case AERelationshipCode.Disabled:
				return annualEnrollmentProps?.disabledChildRelationLabel;
			case AERelationshipCode.StepChild:
				return annualEnrollmentProps?.stepChildRelationLabel;
			case AERelationshipCode.DomesticPartner:
				return annualEnrollmentProps?.domesticPartnerRelationLabel;
			case AERelationshipCode.Spouse:
				return annualEnrollmentProps?.spouseRelationLabel;
			default:
				return '';
		}
	};

	const renderCoverage = (formProps: FormikProps<AnnualEnrollmentFormValues>, groupName: AEPlanType, groupLabel: string) => {

		const selfChecked = formProps?.values[groupName]?.includes(SelfText);
		const selfSecondaryLabelOptions = getSecondaryLabel(selfChecked, groupName, SelfText);
		return (
			<>
				<FieldArray
					name={groupName}
					render={() => (
						<>
							<ContentSpacer>
								<h4>{groupLabel}</h4>
							</ContentSpacer>
							<Field
								disabled
								id={`${groupName}-self`}
								name={groupName}
								label={annualEnrollmentProps?.selfOptionText || 'Self'}
								secondaryLabel={selfSecondaryLabelOptions && selfSecondaryLabelOptions[0]}
								secondaryLabelColor={selfSecondaryLabelOptions && selfSecondaryLabelOptions[1]}
								component='checkBox'								
								value={SelfText}
								checked={selfChecked}
								checkBoxGroup
							/>
							{AEState.AEInfo?.eligible_dependents?.map(dep => {
								const dependentChecked = formProps?.values[groupName]?.includes(dep?.person?._guid);
								const dependentSecondaryLabelOptions = getSecondaryLabel(dependentChecked, groupName, dep?.person?._guid);

								return (
									<Field
										disabled
										id={`${groupName}-${dep.person?._guid}`}
										key={`${groupName}-${dep.person?._guid}`}
										name={groupName}
										label={`${dep?.person?.first} ${dep?.person?.middle} ${dep?.person?.last}  ${dep?.person?.suffix}`}
										secondaryLabel={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[0]}
										secondaryLabelColor={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[1]}
										description={getRelationshipLabel(dep?.relationship_code)}
										component='checkBox'										
										value={dep?.person?._guid}
										checked={dependentChecked}
										checkBoxGroup
									/>
								);
							})}
						</>
					)}
				/>
			</>
		);
	};

	const getPlanLabels = (planType: AEPlanType) => {
		const primaryLabel = planType === AEPlanType.Medical ? annualEnrollmentProps?.medicalPlansLabel : annualEnrollmentProps?.dentalPlansLabel;

		if (!AEState.AEInfo?.offerings) return null;

		return (
			<div>
				<div>{primaryLabel}</div>
				{AEState.AEInfo?.offerings[planType]?.available_tiers.map(tier => <div key={tier}>{getTierLabel(tier, annualEnrollmentProps)}</div>)}
			</div>
		);
	};

	function RenderHeldInReviewMessages() {

		if (!isHeldMessages || isHeldMessages?.length === 0) return null;

		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'warning',
						label: <h5>{isArc ? annualEnrollmentProps.adminConfirmationHeldTitle : annualEnrollmentProps.confirmationHeldTitle}</h5>,
						description:
							<div className='rich-text'>
								<WysiwygWithReactLink content={isArc ? annualEnrollmentProps.adminConfirmationHeldDescription : annualEnrollmentProps.confirmationHeldDescription} />
								{
									isHeldMessages?.map((a, idx) => <p key={idx}>{a}</p>)
								}
							</div>,
					}}
				/>
			</ContentSpacer>
		);
	}

	function RenderButtonBar() {
		if (isHeldForReview) {
			return (
				<ButtonWrapper flexEnd>
					<Link className={buttonClasses.primaryButton} to={isArc ? mapHome : cpgHome}>{isArc ? annualEnrollmentProps.mapHomeLinkText : annualEnrollmentProps.myCpgHomeLinkText}</Link>
				</ButtonWrapper>
			);
		}

		return (
			<DividerSection sectionText={annualEnrollmentProps.abandonFormTitle} removeDivider>
				<ContentSpacer>
					<div className='rich-text'>
						<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps.abandonFormDescription }}></div>
					</div>
				</ContentSpacer>
				<Button buttonStyle='secondary' type='button' onClick={handleOpen} text={annualEnrollmentProps.abandonFormButtonText} />
			</DividerSection>
		);
	}

	return (
		<>
			<RenderHeldInReviewMessages />
			<PageHeader headerText={pageTitle} description={pageDescription} />
			<Formik
				initialValues={initialValues}
				enableReinitialize
				onSubmit={null}
			>
				{(formProps: FormikProps<AnnualEnrollmentFormValues>) => (
					<>
						<Form>
							<Grid>
								<FormWrapper
									formikProps={formProps}
									submitText={annualEnrollmentProps?.coverageContinueButtonText}
									cancelText={annualEnrollmentProps?.coveragePreviousButtonText}
									onCancel={null}
									hideButtons
								>
									<DividerSection removeDivider sectionText={annualEnrollmentProps?.annualEnrollmentOptionsLabel}>
										<h5>{annualEnrollmentProps?.participantSelectionLabel}</h5>
										<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps?.participantSelectionDescription }} />
										<FormGroup>
											<ContentSpacer>
												{renderCoverage(formProps, AEPlanType.Medical, annualEnrollmentProps?.medicalCoverageLabel)}
											</ContentSpacer>
											{renderCoverage(formProps, AEPlanType.Dental, annualEnrollmentProps?.dentalCoverageLabel)}
										</FormGroup>
									</DividerSection>
									<h5>{annualEnrollmentProps?.planSelectionLabel}</h5>
									<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps?.planSelectionDescription }} />
									<div>
										<span>{annualEnrollmentProps?.monthlyPremiumsLabel}</span>
										<Tooltip iconColor={tcsmColors.blue} title={annualEnrollmentProps?.monthlyPremiumsTooltipText} />
									</div>


									<CustomField
										disabled
										name='medicalPlan'
										label={getPlanLabels(AEPlanType.Medical)}
										component={RadioTable}
										options={getOfferingsOptions(AEState?.AEInfo?.offerings?.M, medicalEnrollment?.code, medicalEnrollment?.tier, medicalExpired && medicalEnrollment?.name, annualEnrollmentProps, AEPlanType.Medical, true, labelSetting)}
										formProps={formProps}
									/>

									<CustomField
										disabled
										name='dentalPlan'
										label={getPlanLabels(AEPlanType.Dental)}
										component={RadioTable}
										options={getOfferingsOptions(AEState?.AEInfo?.offerings?.D, dentalEnrollment?.code, dentalEnrollment?.tier, dentalExpired && dentalEnrollment?.name, annualEnrollmentProps, AEPlanType.Dental, true, labelSetting)}
										formProps={formProps}
									/>
									<AnnualEnrollmentSummary
										AEProps={annualEnrollmentProps}
										AEState={AEState}
										selectedDentalPlan={dentalEnrollment?.code}
										selectedMedicalPlan={medicalEnrollment?.code}
										showNotApplicable
									/>
								</FormWrapper>
							</Grid>
						</Form>
					</>
				)}
			</Formik>
			<RenderButtonBar />
			<TcsmModal
				open={open}
				handleClose={handleClose}>
				<>
					<ContentSpacer>
						<div className='rich-text'>
							<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps.abandonFormModelDescription }} />
						</div>
					</ContentSpacer>
					<ButtonWrapper flexEnd>
						<Button type='button' buttonStyle='tertiary' text={annualEnrollmentProps.abandonFormModalCancelText} backgroundColor='#808080' onClick={handleClose} />
						<Button type='button' buttonStyle='primary' text={annualEnrollmentProps.abandonFormModalConfirmText} backgroundColor='#0000FF' onClick={abandonFormClick} />
					</ButtonWrapper>
				</>

			</TcsmModal>
		</>
	);
}


AnnualEnrollmentAdminConfirmation.defaultProps =
{
	basePath: '',
};